import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { errorMessage } from '@/api/errors';
import { AlertBox } from '@/common/AlertBox';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import {
  MemberTagForm,
  type MemberTagFormValues,
} from '@/groups/coordinator/MemberTagForm';
import { useMemberTaggingAlert } from '@/groups/coordinator/MemberTaggingAlertContext';
import type { Person } from '~/hooks/useAsyncPeoplePicker';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/admin/member-tagging/edit/$tagId',
)({
  component: EditMemberTagScreen,
});

function EditMemberTagScreen() {
  const { t } = useTranslation();
  const { groupName, tagId } = Route.useParams();
  const navigate = useNavigate();
  const { setFlash } = useMemberTaggingAlert();

  const { data: groupData, isError: groupFailed } = api.getGroupBySlug.useQuery(
    {
      path: { group_slug: groupName },
    },
  );
  const groupId = groupData?.id ?? 0;
  const numericTagId = Number(tagId);

  // The single-tag read, not the index list: a list row's `member_preview`
  // holds at most three members, and `Update` sends `student_ids` as the full
  // replacement set, so a preview-fed form would drop every assignment past the
  // third.
  const { data: tag, error: readError } = api.getGroupMemberTag.useQuery(
    { path: { group_id: groupId, tag_id: numericTagId } },
    {
      enabled:
        groupId > 0 && Number.isInteger(numericTagId) && numericTagId > 0,
    },
  );
  // A failed group read leaves the tag query permanently disabled, so it has to
  // resolve the screen too — otherwise it is an endless spinner.
  const readFailed = groupFailed || readError != null;

  const initialMembers = useMemo<Person[] | undefined>(
    () => tag?.members.map((m) => ({ id: m.id, name: m.display_name })),
    [tag],
  );

  const {
    mutateAsync: updateTag,
    isPending,
    error: writeError,
  } = api.updateGroupMemberTag.useMutation();

  const goToIndex = (): void => {
    navigate({
      to: '/groups/$groupName/admin/member-tagging/',
      params: { groupName },
    });
  };

  const handleSubmit = async (values: MemberTagFormValues): Promise<void> => {
    try {
      await updateTag({
        path: { group_id: groupId, tag_id: numericTagId },
        body: {
          label: values.tagName,
          student_ids: values.memberIds.map(Number),
        },
      });
      await api.listGroupMemberTags.invalidateQueries();
      // Required: the form seeds its label and member picker once, at mount, so
      // a stale cached detail would show the old label the next time this
      // screen opens.
      await api.getGroupMemberTag.invalidateQueries();
      // Writing the member set changes those students' `tags`, which is what
      // the group's All Students list renders.
      await api.getGroupStudents.invalidateQueries();
      setFlash('updated');
      goToIndex();
    } catch {
      // Rendered from `writeError` below.
    }
  };

  return (
    <>
      <GroupHeaderWrapper>
        {/* "Add Member Tag" on both screens, as in legacy, whose edit
           template prints it too. There's no catalogue string for "Edit
           Member Tag". */}
        <TitleWithQuestion
          title={t('actions.add-member-tag')}
          placement="study_helper_group_settings"
        />
      </GroupHeaderWrapper>
      {readFailed && (
        <AlertBox variant="error">
          {errorMessage(readError, t('profile.member-tag-not-found'))}
        </AlertBox>
      )}
      {writeError && (
        <AlertBox variant="error">
          {errorMessage(writeError, t('wbs.something-went-wrong-please'))}
        </AlertBox>
      )}
      {/* The form mounts only once the whole member set has landed: it seeds
          its picker from `initialMembers` at mount, so rendering it early would
          submit a partially loaded set. */}
      {tag ? (
        <MemberTagForm
          groupId={groupId}
          submitLabel={t('wbs.update')}
          initialTagName={tag.label}
          initialMembers={initialMembers}
          isSubmitting={isPending}
          onBack={goToIndex}
          onSubmit={(values) => {
            void handleSubmit(values);
          }}
        />
      ) : (
        !readFailed && <LoadingSpinner centered />
      )}
    </>
  );
}
